import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

interface LanguageObject {
  name: string;
  flag: string;
  slug: string;
}

interface LanguageSpeechToTextHeroProps {
  languageObj: LanguageObject;
}

export default function LanguageSpeechToTextHero({
  languageObj,
}: LanguageSpeechToTextHeroProps) {
  const { t } = useLocale('ai-transcription');

  return (
    <div className='relative isolate overflow-hidden bg-white dark:bg-slate-900 pt-16 sm:pt-24 lg:pt-32 pb-20'>
      {/* Background elements */}
      <div className='absolute inset-0 -z-10 bg-[radial-gradient(#80808033_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]'></div>
      <div
        className='absolute left-1/2 top-0 -z-10 -translate-x-1/2 transform-gpu blur-3xl'
        aria-hidden='true'
      >
        <div
          className='aspect-[1108/632] w-[69.25rem] bg-gradient-to-tr from-indigo-200 to-primary-100 dark:from-slate-800 dark:to-slate-700 opacity-40 dark:opacity-20'
          style={{
            clipPath:
              'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
          }}
        />
      </div>

      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-3xl text-center'>
          <div className='mb-8 flex justify-center'>
            <div className='relative inline-flex items-center space-x-2 rounded-full px-4 py-1.5 text-sm font-medium leading-6 text-gray-600 dark:text-slate-300 ring-1 ring-gray-900/10 dark:ring-white/10 hover:ring-gray-900/20 dark:hover:ring-white/20 transition-all bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm'>
              <span className='flex h-2 w-2 rounded-full bg-primary-500 animate-pulse'></span>
              <span>
                {languageObj.flag} {t(languageObj.name)}{' '}
                {t('Audio Transcription')}
              </span>
            </div>
          </div>
          <h1 className='text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-6xl mb-6'>
            <span className='block xl:inline'>
              {t('Transcribe')} {t(languageObj.name)} {t('Audio Online')}
            </span>
          </h1>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300 text-balance mx-auto max-w-2xl'>
            {t('Convert')} {t(languageObj.name)}{' '}
            {t(
              'speech to text with our advanced AI transcription service. Perfect accuracy for',
            )}{' '}
            {t(languageObj.name)}{' '}
            {t('audio files, podcasts, meetings, and interviews.')}
          </p>
          <div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              href='/audio'
              className='group relative flex w-full sm:w-auto items-center justify-center rounded-full bg-primary-600 px-8 py-4 text-base font-bold text-white shadow-lg hover:bg-primary-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary-600/30 transition-all duration-300'
            >
              {t('Start Transcribing')} {t(languageObj.name)} {t('Audio')}
              <svg
                className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth='2'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                />
              </svg>
            </Link>
            <Link
              href='#how-it-works'
              className='group flex w-full sm:w-auto items-center justify-center rounded-full bg-white dark:bg-slate-800/80 px-8 py-4 text-base font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/80 hover:-translate-y-1 hover:shadow-md transition-all duration-300 backdrop-blur-sm'
            >
              {t('How it works')}
            </Link>
          </div>
        </div>

        {/* Floating Screenshot */}
        <div className='mt-16 sm:mt-24 lg:mt-20 flex justify-center'>
          <div className='relative rounded-3xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 dark:bg-white/5 dark:ring-white/10 backdrop-blur-xl'>
            <div className='rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/10 dark:bg-slate-900 overflow-hidden'>
              <Image
                src='https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMJA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80'
                alt={`${languageObj.name} speech to text transcription`}
                width={2340}
                height={1560}
                className='w-full object-cover rounded-2xl'
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
